import React, { useState, useMemo, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import {
  FileText, Download, Calendar, Receipt, Search, Loader2,
} from 'lucide-react';

// Invoices tab — a single audit view of every sales document the business
// has issued: B2B invoices, consumer/retail invoices AND credit notes.
// Each row is normalised to { type, invoice_number, date, customer, ref,
// net, vat, gross, pdf_url }. Credit notes are shown as negative amounts so
// the totals reflect net sales. Filterable by date range and document type.

const fmt = (n) => `£${(Number(n) || 0).toFixed(2)}`;

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59); }
function startOfQuarter(d) { return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1); }
function endOfQuarter(d) { return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3 + 3, 0, 23, 59, 59); }
function startOfYear(d) { return new Date(d.getFullYear(), 0, 1); }
function endOfYear(d) { return new Date(d.getFullYear(), 11, 31, 23, 59, 59); }

function toISODate(d) { return d.toISOString().slice(0, 10); }
function parseDate(s) { return s ? new Date(s) : null; }

// Parse a date string that may be ISO (yyyy-mm-dd), UK format (dd/mm/yyyy),
// or a full ISO timestamp. Returns a real Date, or null if unparseable — so
// the audit view shows "—" instead of "Invalid Date" for legacy B2B orders
// whose po_date was stored in dd/mm/yyyy form.
function parseAnyDate(s) {
  if (!s) return null;
  const str = String(s).trim();
  let m = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(.+))?$/);
  if (m) return new Date(str.length === 10 ? str + 'T00:00:00' : str);
  m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return new Date(Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1])));
  const parsed = new Date(str);
  return isNaN(parsed.getTime()) ? null : parsed;
}

// B2B invoices are dated by the date carried in from the Sage import / PO
// parse (po_date), falling back to the system creation date when none was
// imported.
// Resolve the invoice date, preferring the Sage Invoice.Date captured
// during reconciliation, then the imported PO date, then the system date.
function getInvoiceDate(order) {
  return parseAnyDate(order.sage_invoice_date) || parseAnyDate(order.po_date) || parseDate(order.created_date);
}

// Split a gross (inc. VAT) amount into net + VAT at 20%.
function splitVat(gross) {
  const g = Number(gross) || 0;
  const net = Math.round((g / 1.2) * 100) / 100;
  const vat = Math.round((g - net) * 100) / 100;
  return { net, vat };
}

// Leading numeric portion of an invoice number, for sequential sorting.
// Pure-numeric invoice numbers (e.g. 3026027) sort numerically; prefixed
// numbers (e.g. CN-00001) fall back to -Infinity so they sort after numerics.
function numPrefix(s) {
  const m = String(s || '').match(/^\d+/);
  return m ? parseInt(m[0], 10) : -Infinity;
}

const PRESETS = [
  { key: 'all', label: 'All Time' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'this_quarter', label: 'This Quarter' },
  { key: 'last_quarter', label: 'Last Quarter' },
  { key: 'this_year', label: 'This Year' },
  { key: 'last_year', label: 'Last Year' },
  { key: 'custom', label: 'Custom…' },
];

const TYPE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'b2b', label: 'B2B' },
  { key: 'retail', label: 'Consumer' },
  { key: 'credit', label: 'Credit Notes' },
];

const TYPE_BADGE = {
  b2b: 'border-orange-400/30 text-orange-400',
  retail: 'border-blue-400/30 text-blue-400',
  credit: 'border-red-400/30 text-red-400',
};
const TYPE_LABEL = { b2b: 'B2B', retail: 'CONSUMER', credit: 'CREDIT' };

function getRange(preset, customFrom, customTo) {
  const now = new Date();
  switch (preset) {
    case 'this_month': return { from: startOfMonth(now), to: endOfMonth(now) };
    case 'last_month': {
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 15);
      return { from: startOfMonth(lm), to: endOfMonth(lm) };
    }
    case 'this_quarter': return { from: startOfQuarter(now), to: endOfQuarter(now) };
    case 'last_quarter': {
      const lq = new Date(now.getFullYear(), now.getMonth() - 3, 15);
      return { from: startOfQuarter(lq), to: endOfQuarter(lq) };
    }
    case 'this_year': return { from: startOfYear(now), to: endOfYear(now) };
    case 'last_year': return { from: new Date(now.getFullYear() - 1, 0, 1), to: new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59) };
    case 'custom': return { from: customFrom, to: customTo };
    case 'all':
    default: return { from: null, to: null };
  }
}

export default function InvoicesTab() {
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState(toISODate(startOfMonth(new Date())));
  const [customTo, setCustomTo] = useState(toISODate(endOfMonth(new Date())));
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortKey, setSortKey] = useState('invoice');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };
  const [b2bOrders, setB2bOrders] = useState([]);
  const [b2bAccounts, setB2bAccounts] = useState([]);
  const [retailOrders, setRetailOrders] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [loadingExtra, setLoadingExtra] = useState(true);

  // Fetch every sales document source on mount: B2B orders + accounts,
  // consumer/retail orders, and credit notes — so this view is a complete,
  // auditable sales overview rather than B2B-only.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoadingExtra(true);
      try {
        const [b2b, accounts, retail, credits] = await Promise.all([
          base44.entities.B2BOrder.list('-created_date', 500),
          base44.entities.B2BAccount.list('-created_date', 500),
          base44.entities.Order.list('-created_date', 500),
          base44.entities.CreditNote.list('-created_date', 500),
        ]);
        if (!active) return;
        setB2bOrders(b2b || []);
        setB2bAccounts(accounts || []);
        setRetailOrders(retail || []);
        setCreditNotes(credits || []);
      } catch (e) {
        console.warn('[InvoicesTab] load failed', e);
      }
      if (active) setLoadingExtra(false);
    })();
    return () => { active = false; };
  }, []);

  const range = useMemo(() => getRange(preset, parseDate(customFrom), parseDate(customTo)), [preset, customFrom, customTo]);

  // Build a single normalised list from all three sources.
  const documents = useMemo(() => {
    const docs = [];

    // B2B invoices — dated as per the Sage import (po_date = invoice date),
    // falling back to the system creation date when no import date exists.
    for (const o of b2bOrders) {
      if (!o.invoice_url && !o.invoice_number) continue;
      const date = getInvoiceDate(o);
      const account = b2bAccounts.find(a => a.id === o.b2b_account_id);
      docs.push({
        id: o.id,
        type: 'b2b',
        invoice_number: o.sage_invoice_number || o.invoice_number,
        date,
        customer: o.company_name || account?.company_name || '—',
        po_number: o.po_number || '',
        sage_ref: o.sage_invoice_number || '',
        net: Number(o.subtotal_ex_vat) || 0,
        vat: Number(o.vat_amount) || 0,
        gross: Number(o.total_inc_vat) || 0,
        pdf_url: o.invoice_url || '',
      });
    }

    // Consumer / retail invoices
    for (const o of retailOrders) {
      if (!o.invoice_url && !o.invoice_number) continue;
      const { net, vat } = splitVat(o.amount);
      docs.push({
        id: o.id,
        type: 'retail',
        invoice_number: o.sage_invoice_number || o.invoice_number,
        date: parseAnyDate(o.sage_invoice_date) || parseDate(o.created_date),
        customer: o.customer_name || '—',
        po_number: '',
        sage_ref: o.sage_invoice_number || o.source || '',
        net,
        vat,
        gross: Number(o.amount) || 0,
        pdf_url: o.invoice_url || '',
      });
    }

    // Credit notes (negative amounts)
    for (const c of creditNotes) {
      const { net, vat } = splitVat(c.amount);
      docs.push({
        id: c.id,
        type: 'credit',
        invoice_number: c.credit_note_number,
        date: parseDate(c.created_date),
        customer: c.customer_name || '—',
        po_number: c.original_invoice_number || '',
        sage_ref: c.reason || '',
        net: -net,
        vat: -vat,
        gross: -(Number(c.amount) || 0),
        pdf_url: c.pdf_url || '',
      });
    }

    return docs
      .filter(d => {
        if (typeFilter !== 'all' && d.type !== typeFilter) return false;
        if (!range.from || !range.to) return true;
        if (!d.date) return false;
        return d.date >= range.from && d.date <= range.to;
      })
      .filter(d => {
        if (!search) return true;
        const q = search.toLowerCase();
        return d.invoice_number?.toLowerCase().includes(q) ||
          d.customer?.toLowerCase().includes(q) ||
          d.po_number?.toLowerCase().includes(q) ||
          d.sage_ref?.toLowerCase().includes(q) ||
          TYPE_LABEL[d.type]?.toLowerCase().includes(q);
      })
      .sort((a, b) => {
        const dir = sortDir === 'asc' ? 1 : -1;
        let cmp = 0;
        if (sortKey === 'invoice') {
          cmp = numPrefix(a.invoice_number) - numPrefix(b.invoice_number) ||
            String(a.invoice_number || '').localeCompare(String(b.invoice_number || ''));
        } else if (sortKey === 'date') {
          cmp = (a.date?.getTime() || 0) - (b.date?.getTime() || 0);
        } else if (sortKey === 'customer') {
          cmp = String(a.customer || '').localeCompare(String(b.customer || ''));
        } else if (sortKey === 'net') {
          cmp = a.net - b.net;
        } else if (sortKey === 'vat') {
          cmp = a.vat - b.vat;
        } else if (sortKey === 'gross') {
          cmp = a.gross - b.gross;
        }
        return cmp * dir;
      });
  }, [b2bOrders, b2bAccounts, retailOrders, creditNotes, range, search, typeFilter, sortKey, sortDir]);

  // The footer totals are split from the aggregate gross in one pass (VAT
  // rounded up, net = gross − vat) so net + vat = gross exactly and the
  // all-time figures balance to Sage 50. Per-row net/vat stay split per
  // document for the auditable column, but the reconciled total is derived
  // from the single gross sum to avoid per-line rounding drift.
  const totals = useMemo(() => {
    const t = documents.reduce((acc, d) => ({
      gross: acc.gross + (Number(d.gross) || 0),
      count: acc.count + 1,
    }), { gross: 0, count: 0 });
    const vat = Math.ceil((t.gross / 6) * 100) / 100;
    const net = Math.round((t.gross - vat) * 100) / 100;
    return { net, vat, gross: Math.round(t.gross * 100) / 100, count: t.count };
  }, [documents]);

  // Per-type counts for the type filter chips
  const typeCounts = useMemo(() => {
    const counts = { b2b: 0, retail: 0, credit: 0 };
    for (const d of documents) { if (counts[d.type] != null) counts[d.type]++; }
    return counts;
  }, [documents]);

  const rangeLabel = useMemo(() => {
    if (preset === 'all') return 'All Time';
    if (preset === 'custom') {
      const f = parseDate(customFrom); const t = parseDate(customTo);
      if (!f || !t) return 'Custom';
      return `${f.toLocaleDateString('en-GB')} → ${t.toLocaleDateString('en-GB')}`;
    }
    const { from, to } = range;
    if (!from || !to) return '';
    return `${from.toLocaleDateString('en-GB')} → ${to.toLocaleDateString('en-GB')}`;
  }, [preset, customFrom, customTo, range]);

  // Export the currently filtered + sorted documents as a CSV (matches the
  // visible table columns, credit notes shown as negative amounts).
  const exportView = () => {
    const headers = ['Invoice #', 'Order #', 'Type', 'Date', 'Customer', 'PO', 'Net', 'VAT', 'Gross'];
    const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const rows = documents.map((d) => [
      d.invoice_number, d.id, TYPE_LABEL[d.type],
      d.date ? d.date.toLocaleDateString('en-GB') : '',
      d.customer, d.po_number,
      d.net.toFixed(2), d.vat.toFixed(2), d.gross.toFixed(2),
    ]);
    const csv = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="border border-white/10 bg-[#111115] p-4 text-center">
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">DOCUMENTS</p>
          <p className="text-white font-black text-2xl">{totals.count}</p>
        </div>
        <div className="border border-white/10 bg-[#111115] p-4 text-center">
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">NET</p>
          <p className="text-white font-black text-xl">{fmt(totals.net)}</p>
        </div>
        <div className="border border-yellow-400/20 bg-yellow-400/[0.03] p-4 text-center">
          <p className="text-yellow-400/70 text-[10px] font-bold tracking-widest mb-1">VAT</p>
          <p className="text-yellow-400 font-black text-xl">{fmt(totals.vat)}</p>
        </div>
        <div className="border border-green-400/20 bg-green-400/[0.03] p-4 text-center">
          <p className="text-green-400/70 text-[10px] font-bold tracking-widest mb-1">GROSS</p>
          <p className="text-green-400 font-black text-xl">{fmt(totals.gross)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="border border-white/10 bg-[#111115] p-4 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Calendar className="w-4 h-4 text-primary" />
          <p className="text-primary text-[10px] font-bold tracking-widest">DATE RANGE</p>
          <span className="text-white/40 text-[10px] ml-auto">{rangeLabel}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map(p => (
            <button key={p.key} onClick={() => setPreset(p.key)}
              className={`px-3 py-1.5 text-[10px] font-bold tracking-widest border transition-colors ${preset === p.key ? 'border-primary bg-primary text-white' : 'border-white/15 text-white/50 hover:text-white hover:border-white/30'}`}>
              {p.label.toUpperCase()}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1 block">FROM</label>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="seo-input w-44" />
            </div>
            <div>
              <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1 block">TO</label>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="seo-input w-44" />
            </div>
          </div>
        )}
        {/* Type filter */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-white/30 text-[10px] font-bold tracking-widest mr-1">TYPE</span>
          {TYPE_FILTERS.map(t => (
            <button key={t.key} onClick={() => setTypeFilter(t.key)}
              className={`px-3 py-1.5 text-[10px] font-bold tracking-widest border transition-colors ${typeFilter === t.key ? 'border-primary bg-primary text-white' : 'border-white/15 text-white/50 hover:text-white hover:border-white/30'}`}>
              {t.label.toUpperCase()}{t.key !== 'all' && typeCounts[t.key] != null ? ` (${typeCounts[t.key]})` : ''}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-sm min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/55 pointer-events-none" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search invoices…" className="seo-input" style={{ paddingLeft: '2.5rem' }} />
          </div>
          <button
            onClick={exportView}
            disabled={documents.length === 0}
            title="Download the current filtered view as a CSV"
            className="inline-flex items-center gap-1.5 border border-white/20 hover:border-white/40 hover:bg-white/[0.04] text-white/70 hover:text-white font-bold text-[10px] tracking-widest px-4 py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download className="w-3.5 h-3.5" /> EXPORT VIEW (CSV)
          </button>
        </div>
      </div>

      {/* Documents table */}
      {loadingExtra ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : documents.length === 0 ? (
        <div className="text-center py-16 text-white/30 border border-white/10">
          <Receipt className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-bold tracking-widest">NO DOCUMENTS IN THIS RANGE</p>
          <p className="text-xs mt-1">Try a different date range or type filter</p>
        </div>
      ) : (
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/10 bg-[#0d0d0f]">
                {[
                  { key: 'invoice', label: 'INVOICE #', align: 'left' },
                  { key: 'order', label: 'ORDER #', align: 'left', nosort: true },
                  { key: 'date', label: 'DATE', align: 'left' },
                  { key: 'customer', label: 'CUSTOMER', align: 'left' },
                  { key: 'po', label: 'PO', align: 'left', nosort: true },
                  { key: 'net', label: 'NET (£)', align: 'right' },
                  { key: 'vat', label: 'VAT (£)', align: 'right' },
                  { key: 'gross', label: 'GROSS (£)', align: 'right' },
                ].map((h) => (
                  <th
                    key={h.key}
                    onClick={h.nosort ? undefined : () => toggleSort(h.key)}
                    className={`font-bold text-white/40 py-3 px-4 ${h.align === 'right' ? 'text-right' : 'text-left'} ${h.nosort ? '' : 'cursor-pointer hover:text-white select-none whitespace-nowrap'}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {h.label}
                      {!h.nosort && sortKey === h.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </span>
                  </th>
                ))}
                <th className="text-white/40 font-bold text-center py-3 px-4">PDF</th>
              </tr>
            </thead>
            <tbody>
              {documents.map(d => (
                <tr key={`${d.type}-${d.id}`} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className={`text-orange-400 font-mono font-bold ${d.type === 'credit' ? 'text-red-400' : d.type === 'retail' ? 'text-blue-400' : 'text-orange-400'}`}>{d.invoice_number}</span>
                      <span className={`text-[9px] font-bold tracking-widest border px-1.5 py-0.5 ${TYPE_BADGE[d.type]}`}>{TYPE_LABEL[d.type]}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 font-mono" style={{ color: '#D2969F' }}>
                    #{d.id?.slice(-8).toUpperCase()}
                  </td>
                  <td className="py-3 px-4 text-white/60">
                    {d.date ? d.date.toLocaleDateString('en-GB') : '—'}
                  </td>
                  <td className="py-3 px-4 text-white">{d.customer}</td>
                  <td className="py-3 px-4 text-white/50 font-mono">{d.po_number || '—'}</td>
                  <td className="py-3 px-4 text-right text-white/70">{d.net.toFixed(2)}</td>
                  <td className="py-3 px-4 text-right text-yellow-400/80">{d.vat.toFixed(2)}</td>
                  <td className={`py-3 px-4 text-right font-bold ${d.type === 'credit' ? 'text-red-400' : 'text-white'}`}>{d.gross.toFixed(2)}</td>
                  <td className="py-3 px-4 text-center">
                    {d.pdf_url ? (
                      <a href={d.pdf_url} target="_blank" rel="noreferrer" title="Download PDF"
                        className="inline-flex items-center justify-center w-8 h-8 border border-orange-400/30 bg-orange-400/10 hover:bg-orange-400 hover:text-black text-orange-400 transition-colors">
                        <Download className="w-3.5 h-3.5" />
                      </a>
                    ) : <span className="text-white/20">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-white/20 bg-[#0d0d0f]">
                <td colSpan={5} className="py-3 px-4 text-right text-white/50 font-bold text-xs uppercase tracking-widest">
                  Totals ({totals.count})
                </td>
                <td className="py-3 px-4 text-right text-white font-black">{totals.net.toFixed(2)}</td>
                <td className="py-3 px-4 text-right text-yellow-400 font-black">{totals.vat.toFixed(2)}</td>
                <td className="py-3 px-4 text-right text-green-400 font-black">{totals.gross.toFixed(2)}</td>
                <td className="py-3 px-4"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}